import { type Language, getTranslation } from "../lib/i18n";

interface FormData {
    name: string;
    email: string;
    jobTitle: string;
    phoneNumber: string;
    companyName: string;
    companySize: string;
    message: string;
}

export type FormType = 'learn-more' | 'get-in-touch' | 'form' | 'article';

export async function sendFormDataToSlack(
    formData: FormData,
    lang: Language = "en",
    formType: FormType = 'get-in-touch'
): Promise<{ success: boolean; error?: string }> {
  try {
    const translation = getTranslation(lang);

        const formTypeLabels = {
            'learn-more': {
                label: translation.bookDemo,
                emoji: '🎯',
                color: '#36a64f'
            },
            'get-in-touch': {
                label: translation.getInTouch,
                emoji: '💼',
                color: '#2eb886'
            },
            'form': {
                label: 'Form',
                emoji: '📝',
                color: '#4a90e2'
            },
            'article': {
                label: translation.articalModalTitle || 'Article Form',
                emoji: '📰',
                color: '#e74c3c'
            }
        };

        const typeInfo = formTypeLabels[formType];

        const messageBlocks = [
            {
                type: 'header',
                text: {
                    type: 'plain_text',
                    text: `${typeInfo.emoji} ${translation.slackRequestHeader} - ${typeInfo.label}`,
                    emoji: true
                }
            },
            {
                type: 'divider'
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*${translation.slackFormType}:* \`${typeInfo.label}\``
                },
                accessory: {
                    type: 'button',
                    text: {
                        type: 'plain_text',
                        text: typeInfo.emoji,
                        emoji: true
                    },
                    style: 'primary',
                    value: formType
                }
            },
            {
                type: 'divider'
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*👤 ${translation.formName}*\n${formData.name || translation.slackNotSpecified}`
                }
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*📧 ${translation.formEmail}*\n${formData.email || translation.slackNotSpecified}`
                }
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*💼 ${translation.formJobTitle}*\n${formData.jobTitle || translation.slackNotSpecified}`
                }
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*📱 ${translation.formPhoneNumber}*\n${formData.phoneNumber || translation.slackNotSpecified}`
                }
            },
            {
                type: 'divider'
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*🏢 ${translation.formCompanyName}*\n${formData.companyName || translation.slackNotSpecified}`
                }
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `*👥 ${translation.companySize}*\n${formData.companySize || translation.slackNotSpecified}`
                }
            },
            ...(formData.message ? [
                {
                    type: 'divider'
                },
                {
                    type: 'section',
                    text: {
                        type: 'mrkdwn',
                        text: `*💬 ${translation.formMessage}*`
                    }
                },
                {
                    type: 'section',
                    text: {
                        type: 'mrkdwn',
                        text: `\`\`\`${formData.message}\`\`\``
                    }
                }
            ] : []),
            {
                type: 'divider'
            },
            {
                type: 'context',
                elements: [
                    {
                        type: 'mrkdwn',
                        text: `:clock1: ${translation.slackDate}: ${new Date().toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                        })}`
                    }
                ]
            }
        ];
        const apiUrl = '/api/slack-webhook';

        const requestBody = {
            text: `${translation.slackNewRequest} ${formData.name || translation.slackCustomer} - ${typeInfo.label}`,
            blocks: messageBlocks
        };

        console.log('🔵 Slack Request:', {
            apiUrl,
            formType,
            formData: { name: formData.name, email: formData.email }
        });

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
        });

        console.log('🔵 Slack Response:', {
            status: response.status,
            statusText: response.statusText,
            ok: response.ok
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Slack API Error:', {
                status: response.status,
                statusText: response.statusText,
                error: errorText,
                apiUrl
            });

            let errorMessage = '';
            if (response.status === 404) {
                errorMessage = translation.slackWebhookNotFound;
            } else if (response.status === 403) {
                errorMessage = translation.slackWebhookForbidden;
            } else {
                errorMessage = `${translation.slackSendFailed} (${response.status})`;
            }

            throw new Error(errorMessage);
        }

        console.log('✅ Slack Success');
        return { success: true };
    } catch (error: any) {
        console.error('❌ خطأ في إرسال البيانات إلى Slack:', {
            error,
            message: error?.message,
            name: error?.name,
            stack: error?.stack
        });

        const translation = getTranslation(lang);

        if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
            console.error('❌ Network Error - Check proxy configuration');
            return {
                success: false,
                error: translation.slackNetworkError
            };
        }

        return {
            success: false,
            error: error.message || translation.slackGenericError
        };
    }
}
